export type SttResumeTrigger = 'patient_tracker_initialized' | 'recognition_session_missing';

export type SttResumeAttemptResult =
  | 'not_pending'
  | 'already_recording'
  | 'restart_unavailable'
  | 'attempted'
  | 'exhausted';

export interface InterviewSttRecoveryState {
  pending: boolean;
  attempts: number;
  notified: boolean;
}

export interface InterviewSttRecoveryDependencies {
  isRecording: () => boolean;
  isStopping: () => boolean;
  canRestartRecording: () => boolean;
  restartRecording: () => void;
  invalidateSession: (reason: string) => void;
  resetSttUi: () => void;
  resetRecording: () => void | Promise<void>;
  stopSilenceDetection: () => void;
  onAttempt: (trigger: SttResumeTrigger, attempt: number) => void;
  onAutoResumeStarted: () => void;
  onAutoResumeExhausted: () => void;
  onManualRestartRequired: (message?: string) => void;
  onRestartError: (error: unknown) => void;
  onResetError: (context: 'session_missing' | 'disconnect', error: unknown) => void;
  maxAttempts?: number;
}

const DEFAULT_MAX_ATTEMPTS = 5;

/**
 * STT再開の状態と復旧順序を管理する。WebSocketやReact UIへの副作用はdependenciesへ委譲する。
 */
export class InterviewSttRecoveryController {
  private pending = false;
  private attempts = 0;
  private notified = false;
  private readonly maxAttempts: number;

  constructor(private readonly dependencies: InterviewSttRecoveryDependencies) {
    this.maxAttempts = dependencies.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  }

  requestResume(): void {
    this.pending = true;
  }

  isResumePending(): boolean {
    return this.pending;
  }

  markSttAlive(): void {
    this.pending = false;
    this.attempts = 0;
    this.notified = false;
  }

  async handleSessionMissing(message?: string): Promise<void> {
    this.dependencies.invalidateSession('recognition_session_missing');
    const wasIntendingToRecord =
      this.dependencies.isRecording()
      || this.dependencies.isStopping()
      || this.pending;

    this.dependencies.resetSttUi();

    if (!wasIntendingToRecord) {
      this.dependencies.stopSilenceDetection();
      this.dependencies.onManualRestartRequired(message);
      return;
    }

    this.requestResume();
    try {
      await this.dependencies.resetRecording();
    } catch (error) {
      this.dependencies.onResetError('session_missing', error);
    } finally {
      if (this.pending) this.attemptResume('recognition_session_missing');
    }
  }

  async prepareForReconnect(wasRecording: boolean): Promise<void> {
    if (!wasRecording) return;

    this.requestResume();
    try {
      await this.dependencies.resetRecording();
    } catch (error) {
      this.dependencies.onResetError('disconnect', error);
    }
  }

  attemptResume(trigger: SttResumeTrigger): SttResumeAttemptResult {
    if (!this.pending) return 'not_pending';
    if (this.dependencies.isRecording()) return 'already_recording';
    if (!this.dependencies.canRestartRecording()) return 'restart_unavailable';

    if (this.attempts >= this.maxAttempts) {
      this.markSttAlive();
      this.dependencies.onAutoResumeExhausted();
      return 'exhausted';
    }

    this.attempts += 1;
    this.dependencies.onAttempt(trigger, this.attempts);

    try {
      this.dependencies.restartRecording();
      if (!this.notified) {
        this.notified = true;
        this.dependencies.onAutoResumeStarted();
      }
    } catch (error) {
      this.dependencies.onRestartError(error);
    }

    return 'attempted';
  }

  getState(): InterviewSttRecoveryState {
    return {
      pending: this.pending,
      attempts: this.attempts,
      notified: this.notified,
    };
  }
}
